import { motion } from 'framer-motion';
import { Settings, Gamepad2, Save, RefreshCcw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useState } from 'react';
import toast from 'react-hot-toast';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export default function GameSettings() {
    const [ludoEnabled, setLudoEnabled] = useState(true);
    const [autoMatchmaking, setAutoMatchmaking] = useState(true);

    const handleSave = () => {
        toast.success('Game settings saved successfully');
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <Gamepad2 className="w-7 h-7 text-violet-400" />
                        Game Settings
                    </h1>
                    <p className="text-slate-400 mt-1">Configure game rules and settings</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline">
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                    <Button variant="default" onClick={handleSave}>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                    </Button>
                </div>
            </motion.div>

            {/* Game Types */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Gamepad2 className="w-5 h-5 text-violet-400" />
                        Game Types
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                                    <Gamepad2 className="w-5 h-5 text-violet-400" />
                                </div>
                                <div>
                                    <p className="font-medium">Ludo Games</p>
                                    <p className="text-sm text-slate-400">Classic Ludo gameplay</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={ludoEnabled}
                                    onChange={(e) => setLudoEnabled(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between p-4 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                    <Settings className="w-5 h-5 text-blue-400" />
                                </div>
                                <div>
                                    <p className="font-medium">Auto Matchmaking</p>
                                    <p className="text-sm text-slate-400">Automatically match players</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={autoMatchmaking}
                                    onChange={(e) => setAutoMatchmaking(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                            </label>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Game Rules */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Game Rules & Limits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Minimum Bet Amount (₹)</label>
                            <input
                                type="number"
                                defaultValue={10}
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Maximum Bet Amount (₹)</label>
                            <input
                                type="number"
                                defaultValue={10000}
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Max Players per Room</label>
                            <input
                                type="number"
                                defaultValue={4}
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Game Timeout (seconds)</label>
                            <input
                                type="number"
                                defaultValue={60}
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Platform Commission */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Platform Commission</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Ludo Commission (%)</label>
                            <input
                                type="number"
                                defaultValue={5}
                                step="0.1"
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                    </div>
                </Card>
            </motion.div>
        </motion.div>
    );
}
